import { useCallback, useEffect, useRef, useState } from "react";
import type { DrawingReferenceResponse } from "@excalidraw-ms/shared";
import { getDrawingReference } from "../api/backendClient";

/** A read-only, live access check, shared by gallery and editor recipient dialogs. */
export function useDrawingReference({
  drawingId,
  accountId,
  enabled,
  getToken,
}: {
  drawingId?: string;
  accountId?: string;
  enabled: boolean;
  getToken: () => Promise<string>;
}) {
  const [state, setState] = useState<{
    drawingId: string;
    accountId?: string;
    data?: DrawingReferenceResponse;
    error?: string;
  } | null>(null);
  const request = useRef(0);

  const check = useCallback(async () => {
    if (!enabled || !drawingId) throw new Error("Drawing access is not ready");
    const version = ++request.current;
    setState({ drawingId, accountId });
    try {
      const result = await getDrawingReference(await getToken(), drawingId);
      if (version !== request.current) throw new Error("Drawing access check was superseded");
      if (
        result.drawingId !== drawingId ||
        !result.referenceUrl ||
        !result.access ||
        !["owner", "editor", "viewer"].includes(result.access.role) ||
        result.access.canEdit !== (result.access.role !== "viewer")
      ) {
        throw new Error("Invalid drawing access response");
      }
      setState({ drawingId, accountId, data: result });
      return result;
    } catch (error) {
      if (version === request.current) {
        console.error("[useDrawingReference] Drawing access check failed:", error);
        setState({
          drawingId,
          accountId,
          error: "Drawing access could not be checked. Please try again.",
        });
      }
      throw error;
    }
  }, [drawingId, accountId, enabled, getToken]);

  const retry = useCallback(() => {
    // check records the failure for inline recovery; callers requesting consent await it.
    void check().catch(() => undefined);
  }, [check]);

  useEffect(() => {
    if (enabled && drawingId) retry();
    else setState(null);
    return () => {
      request.current += 1;
    };
  }, [drawingId, enabled, retry]);

  const current =
    enabled && state?.drawingId === drawingId && state?.accountId === accountId ? state : null;
  return {
    referenceUrl: current?.data?.referenceUrl ?? null,
    access: current?.data?.access,
    loading: enabled && !current?.data && !current?.error,
    error: current?.error ?? null,
    check,
    retry,
  };
}
